import { c as _c } from "react/compiler-runtime";
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalQueryInvalidation } from '@/hooks/useGlobalQueryInvalidation';
import { useToasts } from '@/hooks/useToasts';
import type { Status } from '@/models/status';
import type { SurveyBase } from '@/models/survey';
import services from '@/services';
import type { UpdateSurveyStatus } from '@/services/surveyService';
const SURVEYS_INFINITE_QUERY_KEY = 'surveys-infinite';
const CLOSED_STATUS_LABEL = 'closed';
const CLOSED_FILTER_JSON_SIGNATURE = '"value":["closed"]';
type SurveysInfiniteData = InfiniteData<{
  data: SurveyBase[];
}>;
function isClosedTabQuery(queryKey: unknown): boolean {
  return JSON.stringify(queryKey).includes(CLOSED_FILTER_JSON_SIGNATURE);
}
function surveyBelongsInThisList(isNewStatusClosed: boolean, isQueryForClosedTab: boolean): boolean {
  if (isNewStatusClosed && !isQueryForClosedTab) return false;
  if (!isNewStatusClosed && isQueryForClosedTab) return false;
  return true;
}
function applyOptimisticStatusUpdate(oldData: SurveysInfiniteData | undefined, surveyId: string, newStatus: Status, isQueryForClosedTab: boolean): SurveysInfiniteData | undefined {
  if (!oldData) return oldData;
  const isNewStatusClosed = newStatus.label === CLOSED_STATUS_LABEL;
  return {
    ...oldData,
    pages: oldData.pages.map(page => ({
      ...page,
      data: page.data.map(survey => survey.id === surveyId ? {
        ...survey,
        status: newStatus
      } : survey).filter(survey => survey.id !== surveyId || surveyBelongsInThisList(isNewStatusClosed, isQueryForClosedTab))
    }))
  };
}
export const useUpdateSurveyStatusMutation = () => {
  const $ = _c(14);
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const {
    invalidateAfterSurveyStatusChange
  } = useGlobalQueryInvalidation();
  const {
    addSuccessToast,
    addErrorToast
  } = useToasts();
  let t0;
  if ($[0] !== queryClient) {
    t0 = async t1 => {
      const {
        surveyId: surveyId_0,
        statusId: statusId_0
      } = t1;
      await queryClient.cancelQueries({
        queryKey: [SURVEYS_INFINITE_QUERY_KEY],
        exact: false
      });
      const allQueries = queryClient.getQueriesData({
        queryKey: [SURVEYS_INFINITE_QUERY_KEY]
      });
      const statuses = queryClient.getQueryData(["statuses"]) ?? [];
      const newStatus = statuses.find(status => status.id === statusId_0);
      if (newStatus) {
        allQueries.forEach(t2 => {
          const [queryKey] = t2;
          queryClient.setQueryData(queryKey, oldData => applyOptimisticStatusUpdate(oldData, surveyId_0, newStatus, isClosedTabQuery(queryKey)));
        });
      }
      return {
        allQueries
      };
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== addSuccessToast || $[3] !== invalidateAfterSurveyStatusChange || $[4] !== t) {
    t1 = async (_result, t2) => {
      const {
        surveyId: surveyId_1
      } = t2;
      await invalidateAfterSurveyStatusChange(surveyId_1);
      addSuccessToast(t("survey.updatedStatus.success", {
        ns: "notifications"
      }));
    };
    $[2] = addSuccessToast;
    $[3] = invalidateAfterSurveyStatusChange;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== addErrorToast || $[7] !== queryClient || $[8] !== t) {
    t2 = (_error, _variables, context) => {
      context?.allQueries.forEach(t3 => {
        const [queryKey_0, data] = t3;
        queryClient.setQueryData(queryKey_0, data);
      });
      addErrorToast(t("survey.updatedStatus.error", {
        ns: "notifications"
      }));
    };
    $[6] = addErrorToast;
    $[7] = queryClient;
    $[8] = t;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t0 || $[11] !== t1 || $[12] !== t2) {
    t3 = {
      mutationFn: _temp,
      onMutate: t0,
      onSuccess: t1,
      onError: t2
    };
    $[10] = t0;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return useMutation(t3);
};
function _temp(t0) {
  const {
    surveyId,
    statusId
  } = t0;
  return services.survey.updateSurveyStatus({
    surveyId,
    statusId
  });
}